import { useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import AuthLayout from "../components/auth/AuthLayout";
import TurnstileWidget, { type TurnstileWidgetRef } from "../components/auth/TurnstileWidget";
import { forgotPassword } from "../api/auth";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const turnstileRef = useRef<TurnstileWidgetRef>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!turnstileToken) {
      setError("Please complete the bot protection challenge.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await forgotPassword({
        email: email.trim().toLowerCase(),
        turnstile_token: turnstileToken,
      });
      setSubmitted(true);
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          data?: {
            detail?: string;
          };
        };
        message?: string;
      };
      // Even on failure, if it's not a turnstile/validation error, protect user enumeration
      setError(axiosErr.response?.data?.detail || "Unable to process request. Please try again later.");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  if (import.meta.env.VITE_LAUNCH_MODE === "true" || import.meta.env.VITE_ACCESS_REQUEST_MODE === "true") {
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthLayout>
      <h2 className="tx-auth-form-title">Reset password</h2>
      <p className="tx-auth-form-subtitle">
        Enter your work email address and we'll send you recovery instructions.
      </p>

      {submitted ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div className="tx-auth-alert tx-auth-alert-success" role="status">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <div>
              <p style={{ fontWeight: 600, marginBottom: 4 }}>Check your inbox</p>
              <p style={{ margin: 0, fontSize: 13, color: "#cbd5e1" }}>
                If an account exists for <strong style={{ color: "#ffffff" }}>{email}</strong>, a password reset link has been dispatched. Please check your spam folder if it doesn't appear within a few minutes.
              </p>
            </div>
          </div>

          <Link
            to="/login"
            className="tx-auth-btn-primary"
            style={{ textDecoration: "none", textAlign: "center", marginTop: 8 }}
          >
            Return to Sign in
          </Link>
        </div>
      ) : (
        <form className="tx-auth-form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="tx-auth-alert tx-auth-alert-error" role="alert">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          <div className="tx-auth-field">
            <label className="tx-auth-label" htmlFor="tx-forgot-email">Account Email</label>
            <input
              id="tx-forgot-email"
              type="email"
              autoComplete="email"
              required
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="tx-auth-input"
              disabled={loading}
            />
          </div>

          {/* Turnstile Bot Protection */}
          <TurnstileWidget
            ref={turnstileRef}
            onSuccess={(token) => setTurnstileToken(token)}
            onError={() => setTurnstileToken("")}
            onExpire={() => setTurnstileToken("")}
          />

          <button
            type="submit"
            className="tx-auth-btn-primary"
            disabled={loading}
            id="btn-forgot-password"
          >
            {loading ? (
              <>
                <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
                <span>Sending Instructions...</span>
              </>
            ) : (
              "Send Recovery Link"
            )}
          </button>

          <div className="tx-auth-card-footer">
            Remember your password?{" "}
            <Link to="/login" className="tx-auth-link">
              Sign in
            </Link>
          </div>
        </form>
      )}
    </AuthLayout>
  );
}
